import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FcCancel } from 'react-icons/fc';
import { useDispatch, useSelector } from 'react-redux';

import { smsCustomTextType } from '../../config';
import Loader from '../../_components/Loader/Loader';
import ButtonSpinner from '../../_components/ButtonSpinner';
import { usePreloadImages } from '../../_hooks/usePreloadImages';
import { ChatBubble } from '../../_components/OktaLogin/ChatBubble';
import { LegalDisclaimer } from '../../_components/OktaLogin/LegalDisclaimer';
import { setLoginLoading } from '../../_actions/login.actions';

// Assets
import './index.scss';

import { FaqModal } from '../../_components/OktaLogin/FaqModal';
import { useOktaAuth } from '../../_hooks/useOktaAuth';
import NoErrorLoader from '../../_components/NoErrorLoader/NoErrorLoader';
import mlbLogo from '../../_assets/mlbLogoNew.png';
import bigALogo from '../../_assets/big-a-pass-logo.png';
import { useSalesChannel } from '../../_hooks/useSalesChannel';

import { upcomingEventsSelector } from '../../_selectors';
import { useIsOktaSSOforPassSubscription } from '../../_hooks/useIsOktaSSOforPassSubscription';
import { UpcomingMatchesModal } from '../../_components/OktaLogin/UpcomingMatchesModal';
import { VideoModal } from '../../_components/VideoModal';
import { useQueryparam } from '../../_hooks/useQueryparam';
import { BallparkRegistrationDialog } from '../../_components/BallparkRegistrationDialog';
import { okta } from '../../_services/okta.service';

const OktaLoginPage: React.FC = () => {
    // imagesLoaded = false until all TTB UI images are loaded
    const { preloadImages, imagesLoaded } = usePreloadImages();

    const [showFaqModal, setShowFaqModal] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [showUpcomingMatchesModal, setShowUpcomingMatchesModal] = useState(false);
    const [showBallparkDialog, setShowBallparkDialog] = useState(false);
    const source = sessionStorage.getItem('source');

    const dispatch = useDispatch();
    const { textBuyUI, textBuySSO, salesChannelName, salesChannelConfig, sourceSpecificSliderImages } =
        useSalesChannel();
    const { data: eventsData } = useSelector(upcomingEventsSelector);

    const query = useQueryparam();
    const mlbamOkta = query.get('x-mlbam-okta');
    const mlbEmail = query.get('x-mlbam-mai');

    const { oktaProfile, loading, error, signIn, ballparkSignIn, data: userData } = useOktaAuth();
    const isOktaPass = useIsOktaSSOforPassSubscription();

    console.log('oktaProfile', oktaProfile);

    const customChatBubbleText =
        salesChannelConfig &&
        salesChannelConfig.smsTextOverrides &&
        (source ? (salesChannelConfig.smsTextOverrides[source] as smsCustomTextType) : null);

    useEffect(() => {
        if (!textBuyUI) {
            return;
        }
        /**
         * Preload
         */
        preloadImages(
            [textBuyUI.signupBackgroundUrl, textBuyUI.tmLogoUrl, textBuyUI.tmVideoImage].filter(Boolean).map(String),
        );

        if (!mlbamOkta && !mlbEmail) {
            window.location.href = 'https://www.mlb.com/angels/apps/mlb-app';
        }
    }, [textBuyUI]);

    useEffect(() => {
        if (userData && userData.signupToken) {
            if (userData.completedAt) {
                window.location.href = `/pass/myaccount/payment`;
            } else {
                window.location.href = `/pass/createaccount/${userData.signupToken}`;
            }
        }
    }, [userData]);

    /**
     * Watch for the TM profile and only fire when images and profile loads.
     */
    useEffect(() => {
        if (!imagesLoaded || !oktaProfile) {
            return;
        }

        // Otherwise, just sign in...
        signIn(null);
    }, [oktaProfile, imagesLoaded]);

    useEffect(() => {
        if (error?.message === 'No Provenue profile found') {
            setShowBallparkDialog(true);
        }
    }, [error]);

    const handleBallparkRegistration = async (firstName: string, lastName: string) => {
        if (textBuyUI?.salesChannelId) {
            dispatch(
                okta.ballparkRegister(textBuyUI.salesChannelId, {
                    firstName,
                    lastName,
                    mlbamOkta,
                    mlbEmail,
                }),
            );
        }
        setShowBallparkDialog(false);
    };

    if (!imagesLoaded) {
        return <NoErrorLoader loading={!imagesLoaded} />;
    }
    if (!textBuyUI) {
        return <Loader message="Unable to fetch salesChannel data!" />;
    }

    return (
        <div className={'okta-login'}>
            <div
                className="w-100 flex min-h-screen fw-container bg-center bg-cover md:py-4"
                style={{
                    backgroundImage: `url(${textBuyUI?.signupBackgroundUrl})`,
                }}
            >
                <div className="container w-full my-auto md:max-w-xl px-0 mx-auto md:border-2 border-white md:rounded-xl">
                    <div
                        className="py-5 px-10 md:rounded-t-xl bg-blend-overlay"
                        style={{
                            background: textBuyUI?.tmLoginBg,
                        }}
                    >
                        <div className="mw-100">
                            <div className="md:w-96 mx-auto blur text-center">
                                <img
                                    src={bigALogo}
                                    style={{ maxWidth: '100%' }}
                                    className={'okta-team-logo mx-auto'}
                                    alt={''}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="py-3 px-10">
                        <div className={'grid mx-auto max-w-sm'}>
                            <ChatBubble id="1">
                                {customChatBubbleText?.sms1 ||
                                    textBuySSO?.sms1 ||
                                    'Interested in getting access to last-minute tickets and more?'}
                            </ChatBubble>

                            <ChatBubble id="3">
                                {customChatBubbleText?.sms3 ||
                                    textBuySSO?.sms3 ||
                                    "Watch this video, it's as easy as sending a text message!"}
                                {isOktaPass ? (
                                    <div
                                        className={'w-full mt-1 cursor-pointer'}
                                        onClick={() => setShowUpcomingMatchesModal(true)}
                                    >
                                        <img
                                            src={textBuyUI?.tmVideoImage}
                                            className={'rounded-lg w-full'}
                                            alt={'Upcoming Matches'}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        className={'w-full mt-1 cursor-pointer'}
                                        onClick={() => setShowVideoModal(true)}
                                    >
                                        <img
                                            src={textBuyUI?.tmVideoImage}
                                            className={'rounded-lg w-full'}
                                            alt={'Video'}
                                        />
                                    </div>
                                )}
                            </ChatBubble>
                        </div>
                    </div>
                    <div
                        className="py-3 px-4"
                        style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        }}
                    >
                        <div className="text-center m-2">
                            <div className={'flex justify-content-center w-full'}>
                                <div className={'inline-block self-center mr-3 okta-login-faq-btn'}>
                                    <span
                                        className={'inline-block bg-white cursor-pointer bg-blue-600 rounded-full'}
                                        onClick={() => setShowFaqModal(true)}
                                        style={{ width: '18px', height: '18px' }}
                                    >
                                        <svg
                                            style={{ marginBottom: '8px' }}
                                            width="4"
                                            height="12"
                                            viewBox="0 0 4 12"
                                            fill="blue"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M0.875 1.875C0.875 1.57663 0.993526 1.29048 1.2045 1.07951C1.41548 0.868527 1.70163 0.75 2 0.75C2.29837 0.75 2.58452 0.868527 2.79549 1.07951C3.00647 1.29048 3.125 1.57663 3.125 1.875C3.125 2.17337 3.00647 2.45952 2.79549 2.67049C2.58452 2.88147 2.29837 3 2 3C1.70163 3 1.41548 2.88147 1.2045 2.67049C0.993526 2.45952 0.875 2.17337 0.875 1.875ZM1.4375 4.5H2.5625C2.66562 4.5 2.75 4.58437 2.75 4.6875V11.0625C2.75 11.1656 2.66562 11.25 2.5625 11.25H1.4375C1.33437 11.25 1.25 11.1656 1.25 11.0625V4.6875C1.25 4.58437 1.33437 4.5 1.4375 4.5Z"
                                                fill="#026BDE"
                                            />
                                        </svg>
                                    </span>
                                </div>

                                <button
                                    className="okta-login-btn bg-gray-100 text-sm md:text-base font-medium leading-6 rounded border-gray-200 py-2 hover:bg-gray-800 hover:text-white focus:outline-none"
                                    style={{ maxWidth: '100%', width: '300px' }}
                                    disabled={loading}
                                    onClick={() => {
                                        dispatch(setLoginLoading());
                                        ballparkSignIn({
                                            mlbamOkta,
                                            mlbEmail,
                                        });
                                    }}
                                >
                                    <img
                                        src={mlbLogo}
                                        alt={'MLB'}
                                        style={{ height: '24px' }}
                                        className={`h-4 inline-block mb-1 mr-1 ${loading ? 'invisible' : ''}`}
                                    />
                                    <span
                                        style={{
                                            padding: '3px',
                                            display: 'inline-block',
                                        }}
                                    >
                                        <ButtonSpinner show={loading} buttonLabel={'Continue'} />
                                    </span>
                                </button>
                            </div>
                            <p className={'text-white mt-3 text-sm'}>
                                You will be taken to the subscription page after clicking Continue.
                            </p>
                        </div>
                        <div className="text-center align-items-center">
                            {!isEmpty(error) && error?.message !== 'No Provenue profile found' && (
                                <div
                                    className={
                                        'w-full p-4 flex content-evenly flex-nowrap mx-auto bg-gray-200 text-red-700 rounded'
                                    }
                                    style={{ maxWidth: '380px' }}
                                >
                                    <div className={'self-center text-center'} style={{ width: '80px' }}>
                                        <FcCancel size={'24'} />
                                    </div>
                                    <div className={''}>
                                        {error?.message || error?.error_description || 'Error while signing in.'}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <LegalDisclaimer
                        salesChannelName={salesChannelName}
                        tosURL={salesChannelConfig.termsOfUse}
                        privacyPolicyURL={salesChannelConfig.privacyPolicy}
                        hidePogoseatBranding={salesChannelConfig.hidePogoseatBranding}
                    />
                </div>

                <FaqModal isOpen={showFaqModal} onRequestClose={() => setShowFaqModal(false)} />

                <VideoModal
                    isOpen={showVideoModal}
                    onRequestClose={() => setShowVideoModal(false)}
                    videoUrl={textBuyUI.tmVideoUrl}
                    sliderImages={sourceSpecificSliderImages}
                />

                <UpcomingMatchesModal
                    isOpen={showUpcomingMatchesModal}
                    onRequestClose={() => setShowUpcomingMatchesModal(false)}
                    events={eventsData}
                />

                <BallparkRegistrationDialog
                    isOpen={showBallparkDialog}
                    onCancel={() => setShowBallparkDialog(false)}
                    onContinue={handleBallparkRegistration}
                />
            </div>
        </div>
    );
};

export default OktaLoginPage;
